/**
 * @file CorsProxyInterceptorSlim.js
 * @description Interceptor Axios minimalista y robusto para resolver errores CORS
 *              aplicando la lógica centralizada de proxy definida en CorsProxyService.
 *              Cumple con principios SOLID: una única responsabilidad (coordinar),
 *              delegando el resto a servicios especializados ya existentes.
 *
 * Uso:
 *    import CorsProxyInterceptorSlim from './CorsProxyInterceptorSlim';
 *    const interceptor = new CorsProxyInterceptorSlim(proxyService, { debug: true });
 *    interceptor.register(axiosInstance);
 */

import axios from 'axios';
import corsProxyService from './CorsProxyService';
import corsProxyConfig from './CorsProxyConfig';
import telemetryService, { TelemetryEvents } from '../../services/TelemetryService';

// Fallback en caso de que TelemetryEvents no tenga las keys
const safeEvent = (name) => (TelemetryEvents && TelemetryEvents[name]) || name;
const EVT_PROXY_USED          = safeEvent('PROXY_USED');
const EVT_PROXY_BYPASSED      = safeEvent('PROXY_BYPASSED');
const EVT_CORS_RETRY_ATTEMPT  = safeEvent('CORS_RETRY_ATTEMPT');
const EVT_CORS_RETRY_SUCCESS  = safeEvent('CORS_RETRY_SUCCESS');
const EVT_CORS_RETRY_FAILURE  = safeEvent('CORS_RETRY_FAILURE');

const DEFAULT_MAX_RETRIES = 1;

/**
 * Determina si el error recibido es muy probable un problema de CORS / red.
 * @param {any} error - Error lanzado por axios.
 */
function isLikelyCorsError(error) {
  if (!error) return false;
  // Network error (sin response) o status 0
  if (!error.response) return true;
  const status = error.response.status;
  return status === 0 || status === 401 || status === 403;
}

class CorsProxyInterceptorSlim {
  /**
   * @param {CorsProxyService} proxyService - Servicio de proxy, por defecto el singleton.
   * @param {Object}   [options]
   * @param {number}   [options.maxRetries=1]      - Nº máximo de reintentos cuando haya error CORS.
   * @param {boolean}  [options.debug=false]       - Modo verbose.
   */
  constructor(proxyService = corsProxyService, { maxRetries = DEFAULT_MAX_RETRIES, debug = false } = {}) {
    if (!proxyService || typeof proxyService.processRequest !== 'function') {
      throw new Error('[CorsProxyInterceptorSlim] proxyService inválido');
    }
    this.proxyService = proxyService;
    this.maxRetries   = maxRetries;
    this.debug        = debug;
    // IDs de interceptores para poder eyectar
    this.reqId = null;
    this.resId = null;
    this.instance = null;
  }

  /**
   * Registra los interceptores en la instancia de axios indicada.
   * @param {import('axios').AxiosInstance} axiosInstance - Instancia sobre la que se registrarán.
   */
  register(axiosInstance = axios) {
    if (!axiosInstance?.interceptors) {
      throw new Error('[CorsProxyInterceptorSlim] axiosInstance inválida');
    }
    if (this.reqId !== null || this.resId !== null) return this; // ya registrados
    const retryMap = new WeakMap(); // config -> attempts

    /* ---------- REQUEST ---------- */
    this.reqId = axiosInstance.interceptors.request.use((config) => {
      const originalUrl = config.url;
      if (!originalUrl) return config;

      // Resolver URL completa (considerando baseURL) para análisis más preciso
      let fullUrl;
      try {
        // Si originalUrl ya es absoluta, URL lo mantendrá. De lo contrario, usa baseURL.
        const base = config.baseURL || window.location.origin;
        fullUrl = new URL(originalUrl, base).toString();
      } catch (_) {
        fullUrl = originalUrl; // fallback
      }

      // Forzar proxy si el dominio así lo requiere
      if (corsProxyConfig.shouldAlwaysUseProxy(fullUrl)) {
        const { url, options } = this.proxyService.processRequest(fullUrl, { headers: config.headers });
        if (this.debug) console.debug('[CORS] Proxy forced:', originalUrl, '→', url);
        if (telemetryService?.trackEvent) {
          telemetryService.trackEvent(EVT_PROXY_USED, { domain: (() => {
            try { return new URL(originalUrl, window.location.origin).hostname; } catch (_) { return originalUrl; }
          })(), forced: true });
        }
        return { ...config, url, headers: { ...(config.headers || {}), ...(options.headers || {}) }, _proxied: true };
      }

      // Si no necesita proxy -> event opcional
      if (telemetryService?.trackEvent) {
        telemetryService.trackEvent(EVT_PROXY_BYPASSED, { domain: (() => {
            try { return new URL(originalUrl, window.location.origin).hostname; } catch (_) { return originalUrl; }
          })() });
      }
      return config;
    });

    /* ---------- RESPONSE ERROR ---------- */
    this.resId = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const cfg = error?.config;
        if (!cfg || cfg._proxied) return Promise.reject(error);
        const attempts = retryMap.get(cfg) || 0;
        if (attempts >= this.maxRetries) return Promise.reject(error);
        if (!isLikelyCorsError(error)) return Promise.reject(error);

        retryMap.set(cfg, attempts + 1);
        const { url, options } = this.proxyService.processRequest(cfg.url, { headers: cfg.headers });
        if (this.debug) console.warn(`[CORS] Retry via proxy (${attempts + 1}/${this.maxRetries}):`, cfg.url, '→', url);
        telemetryService?.trackEvent?.(EVT_CORS_RETRY_ATTEMPT, { domain: (() => {
            try { return new URL(cfg.url, window.location.origin).hostname; } catch (_) { return cfg.url; }
          })(), attempt: attempts + 1 });

        const newCfg = {
          ...cfg,
          url,
          headers: { ...(cfg.headers || {}), ...(options.headers || {}) },
          _proxied: true
        };
        try {
          const res = await axiosInstance.request(newCfg);
          telemetryService?.trackEvent?.(EVT_CORS_RETRY_SUCCESS, { domain: (() => {
            try { return new URL(cfg.url, window.location.origin).hostname; } catch (_) { return cfg.url; }
          })() });
          return res;
        } catch (err) {
          telemetryService?.trackEvent?.(EVT_CORS_RETRY_FAILURE, { domain: (() => {
            try { return new URL(cfg.url, window.location.origin).hostname; } catch (_) { return cfg.url; }
          })() });
          return Promise.reject(err);
        }
      }
    );

    this.instance = axiosInstance;
    return this;
  }

  /**
   * Elimina los interceptores previamente registrados.
   */
  unregister() {
    if (!this.instance?.interceptors) return this;
    if (this.reqId !== null) this.instance.interceptors.request.eject(this.reqId);
    if (this.resId !== null) this.instance.interceptors.response.eject(this.resId);
    this.reqId = this.resId = null;
    this.instance = null;
    return this;
  }
}

export default CorsProxyInterceptorSlim;
